import { BigDecimal } from "@solomon-labs/bigdecimal";

import type { StakingVaultState, UserUnstakingQueueEntry } from "@/stores/useStakingSessionStore";

export interface OptimisticQueueEntry extends UserUnstakingQueueEntry {
    id: string;
    createdAt: number;
}

export interface InternalStakingVaultState {
    isStakeBlacklistedLoaded: boolean;
    isUserUnstakingQueueLoaded: boolean;
    isStakeBlacklisted: boolean;
    fetchedUserUnstakingQueue: UserUnstakingQueueEntry[] | undefined;
    optimisticQueueEntries: OptimisticQueueEntry[];
    optimisticClaimReduction: BigDecimal;
    optimisticClaimTargetQueue: UserUnstakingQueueEntry[] | undefined;
    mutationVersion: number;
    activeReconciliationVersion: number | null;
}

export const RECONCILIATION_INTERVAL_MS = 2000;
export const RECONCILIATION_TIMEOUT_MS = 15000;
export const OPTIMISTIC_TIMESTAMP_TOLERANCE_S = 120;
export const OPTIMISTIC_AMOUNT_TOLERANCE = new BigDecimal("0.000001");

export const INITIAL_INTERNAL_STAKING_VAULT_STATE = (): InternalStakingVaultState => ({
    isStakeBlacklistedLoaded: false,
    isUserUnstakingQueueLoaded: false,
    isStakeBlacklisted: false,
    fetchedUserUnstakingQueue: undefined,
    optimisticQueueEntries: [],
    optimisticClaimReduction: new BigDecimal(0),
    optimisticClaimTargetQueue: undefined,
    mutationVersion: 0,
    activeReconciliationVersion: null
});

let optimisticQueueEntryCounter = 0;

export function generateOptimisticEntryId(): string {
    return `${Date.now()}-${optimisticQueueEntryCounter++}`;
}

export function resetOptimisticEntryCounter(): void {
    optimisticQueueEntryCounter = 0;
}

export function getNowSeconds(): number {
    return Math.floor(Date.now() / 1000);
}

export function isQueueEntryAvailable(entry: UserUnstakingQueueEntry, now: number): boolean {
    return entry.timestamp === 0 || entry.timestamp <= now;
}

export function buildBaseQueue(
    fetchedQueue: UserUnstakingQueueEntry[] | undefined,
    optimisticQueueEntries: OptimisticQueueEntry[]
): UserUnstakingQueueEntry[] | undefined {
    const baseQueue = [
        ...(fetchedQueue ?? []),
        ...optimisticQueueEntries.map(({ amount, timestamp }) => ({
            amount,
            timestamp
        }))
    ];

    return baseQueue.length > 0 ? baseQueue : undefined;
}

export function applyClaimReductionToQueue(
    queue: UserUnstakingQueueEntry[] | undefined,
    reduction: BigDecimal,
    now: number = getNowSeconds()
): UserUnstakingQueueEntry[] | undefined {
    if (!queue || queue.length === 0) {
        return undefined;
    }

    if (!reduction.gt(0)) {
        return queue;
    }

    let remainingToRemove = reduction;
    const nextQueue: UserUnstakingQueueEntry[] = [];

    for (const entry of queue) {
        const isAvailable = isQueueEntryAvailable(entry, now);

        if (!isAvailable || !remainingToRemove.gt(0)) {
            nextQueue.push(entry);
            continue;
        }

        if (entry.amount.gt(remainingToRemove)) {
            nextQueue.push({
                ...entry,
                amount: entry.amount.subtract(remainingToRemove)
            });
            remainingToRemove = new BigDecimal(0);
            continue;
        }

        remainingToRemove = remainingToRemove.subtract(entry.amount);
    }

    return nextQueue.length > 0 ? nextQueue : undefined;
}

export function areQueuesEqual(
    left: UserUnstakingQueueEntry[] | undefined,
    right: UserUnstakingQueueEntry[] | undefined
): boolean {
    const normalizedLeft = left && left.length > 0 ? left : undefined;
    const normalizedRight = right && right.length > 0 ? right : undefined;

    if (!normalizedLeft && !normalizedRight) {
        return true;
    }

    if (!normalizedLeft || !normalizedRight) {
        return false;
    }

    if (normalizedLeft.length !== normalizedRight.length) {
        return false;
    }

    return normalizedLeft.every(
        (entry, index) =>
            entry.timestamp === normalizedRight[index]?.timestamp && entry.amount.eq(normalizedRight[index].amount)
    );
}

export function areQueueAmountsClose(left: BigDecimal, right: BigDecimal): boolean {
    return left.subtract(right).abs().lte(OPTIMISTIC_AMOUNT_TOLERANCE);
}

export function reconcileOptimisticQueueEntries(
    fetchedQueue: UserUnstakingQueueEntry[] | undefined,
    optimisticQueueEntries: OptimisticQueueEntry[]
): OptimisticQueueEntry[] {
    if (!fetchedQueue || fetchedQueue.length === 0 || optimisticQueueEntries.length === 0) {
        return optimisticQueueEntries;
    }

    const matchedIds = new Set<string>();
    const optimisticByAge = [...optimisticQueueEntries].sort((left, right) => left.createdAt - right.createdAt);

    for (const fetchedEntry of fetchedQueue) {
        if (fetchedEntry.timestamp === 0) {
            continue;
        }

        const matchedEntry = optimisticByAge.find(
            (optimisticEntry) =>
                !matchedIds.has(optimisticEntry.id) &&
                areQueueAmountsClose(optimisticEntry.amount, fetchedEntry.amount) &&
                Math.abs(optimisticEntry.timestamp - fetchedEntry.timestamp) <= OPTIMISTIC_TIMESTAMP_TOLERANCE_S
        );

        if (matchedEntry) {
            matchedIds.add(matchedEntry.id);
        }
    }

    return optimisticQueueEntries.filter((entry) => !matchedIds.has(entry.id));
}

export function getVisibleQueue(
    vault: InternalStakingVaultState,
    now: number = getNowSeconds()
): UserUnstakingQueueEntry[] | undefined {
    return applyClaimReductionToQueue(
        buildBaseQueue(vault.fetchedUserUnstakingQueue, vault.optimisticQueueEntries),
        vault.optimisticClaimReduction,
        now
    );
}

export function toPublicVaultState(vault: InternalStakingVaultState): StakingVaultState {
    return {
        isStakeBlacklistedLoaded: vault.isStakeBlacklistedLoaded,
        isUserUnstakingQueueLoaded: vault.isUserUnstakingQueueLoaded,
        isStakeBlacklisted: vault.isStakeBlacklisted,
        userUnstakingQueue: getVisibleQueue(vault)
    };
}

export function reconcileVaultAfterFetch(vault: InternalStakingVaultState): InternalStakingVaultState {
    const optimisticQueueEntries = reconcileOptimisticQueueEntries(
        vault.fetchedUserUnstakingQueue,
        vault.optimisticQueueEntries
    );
    const baseQueue = buildBaseQueue(vault.fetchedUserUnstakingQueue, optimisticQueueEntries);
    const shouldClearClaimReduction =
        !!vault.optimisticClaimTargetQueue && areQueuesEqual(baseQueue, vault.optimisticClaimTargetQueue);

    return {
        ...vault,
        optimisticQueueEntries,
        optimisticClaimReduction: shouldClearClaimReduction ? new BigDecimal(0) : vault.optimisticClaimReduction,
        optimisticClaimTargetQueue: shouldClearClaimReduction ? undefined : vault.optimisticClaimTargetQueue
    };
}
